import * as Yup from 'yup'
import { EulaType, ScheduleType } from '../../types/flyout'
import _, { isNil } from 'lodash'
import {
  awsAccountIdRegex,
  emptySpaceCharsAtStartAndEndRegex,
} from './constants'
import {
  awsAccountIdFieldValidationMessage,
  awsRegexValidationMessage,
  enterPriceMsg,
  enterQuantityMsg,
  mandatoryFieldMessage,
  missignFieldMessage,
  quantityDecimalMsg,
  quantityMoreThan0Msg,
  quantityNegativeMsg,
  threeDecimalMessage,
} from './messagesContants'
import {
  type CustomStakeholder,
  metaDataItemValidationSchema,
} from './PrivateOfferContainerFormikValidationSchema'
import { DateTime } from 'luxon'
import { PlanType } from '@labrav/react-components'
import { DiscountType } from '../../admin/modules/resaleAuthorizations/reducer'
import { buildOfferRecipientsValidation } from './offerRecipientYupValidation'
import './globalValidations'

const offerRecipientMessages = {
  firstNameRequired: missignFieldMessage,
  lastNameRequired: missignFieldMessage,
  emailRequired: 'Please provide the email of your offer recipient. ',
  emailInvalid: 'Enter valid email.',
  duplicateEmail: 'Duplicate emails are not allowed.',
}
declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
  }
}

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = _.get(item, propertyPath)
      if (
        propertyValue &&
        _.filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!_.isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

export const getResaleAuthFormikValidationSchema = (
  productType: PlanType,
  dimensionsOptions?: any,
  _isExternal?: boolean
) => {
  const dimensionsSaasValidation = Yup.array().when(
    ['discountType'],
    (values, schema) => {
      const discountType = values as any

      const withUnique =
        discountType !== DiscountType.USAGE_PRICING
          ? schema.uniquePropertyCustomType(
              'label',
              'Display name must be unique',
              dimensionsOptions
            )
          : schema

      // 🔹 INSTALLMENT
      if (discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS) {
        return withUnique.of(
          Yup.object().shape({
            type: Yup.string(),
            name: Yup.string().when('type', {
              is: (type: string) => type !== 'standard',
              then: Yup.string().required('API identifier is required'),
              otherwise: Yup.string().nullable().notRequired(),
            }),
            label: Yup.string().when('type', {
              is: (type: string) => type !== 'standard',
              then: Yup.string()
                .required('Display name is required')
                .matches(
                  emptySpaceCharsAtStartAndEndRegex,
                  `${mandatoryFieldMessage}.`
                )
                .max(80, 'Display name must be 1 to 80 characters'),
              otherwise: Yup.string().required(
                'Please choose at least one product dimension to proceed.'
              ),
            }),
            labelDescription: Yup.string().when('type', {
              is: (type: string) => type !== 'standard',
              then: Yup.string()
                .required('Description is required')
                .matches(
                  emptySpaceCharsAtStartAndEndRegex,
                  `${mandatoryFieldMessage}.`
                )
                .max(1000, 'Description must be 1 to 1000 characters'),
              otherwise: Yup.string().nullable().notRequired(),
            }),
            quantity: Yup.number().when('isAdditionalUsageAllowed', {
              is: false,
              then: Yup.number()
                .typeError(enterQuantityMsg)
                .required(enterQuantityMsg)
                .positive(quantityNegativeMsg)
                .integer(quantityDecimalMsg)
                .min(1, quantityMoreThan0Msg),
              otherwise: Yup.number().nullable().notRequired(),
            }),
            isAdditionalUsageAllowed: Yup.boolean(),
            prices: Yup.object().when('isAdditionalUsageAllowed', {
              is: (val: any) => val === true,
              then: Yup.object().shape({
                additionalUsagePrice: Yup.number()
                  .typeError('Please enter a price')
                  .required('Please enter a price')
                  .min(0, 'Price cannot be negative')
                  .checkThreeDecimalpointValidation({
                    message: threeDecimalMessage,
                  }),
              }),
              otherwise: Yup.object().nullable(),
            }),
          })
        )
      }

      // 🔹 UPFRONT
      if (discountType === DiscountType.INDIVIDUAL_PRICING) {
        return withUnique.of(
          Yup.object().shape({
            type: Yup.string(),
            name: Yup.string().when('type', {
              is: (type: string) => type !== 'standard',
              then: Yup.string().required('API identifier is required'),
              otherwise: Yup.string().nullable().notRequired(),
            }),
            label: Yup.string().when('type', {
              is: (type: string) => type !== 'standard',
              then: Yup.string()
                .required('Display name is required')
                .matches(
                  emptySpaceCharsAtStartAndEndRegex,
                  `${mandatoryFieldMessage}.`
                )
                .max(80, 'Display name must be 1 to 80 characters'),
              otherwise: Yup.string().required(
                'Please choose at least one product dimension to proceed.'
              ),
            }),
            labelDescription: Yup.string().when('type', {
              is: (type: string) => type !== 'standard',
              then: Yup.string()
                .required('Description is required')
                .matches(
                  emptySpaceCharsAtStartAndEndRegex,
                  `${mandatoryFieldMessage}.`
                )
                .max(1000, 'Description must be 1 to 1000 characters'),
              otherwise: Yup.string().nullable().notRequired(),
            }),
            isAdditionalUsageAllowed: Yup.boolean(),
            // price: Yup.number()
            //   .typeError(enterPriceMsg)
            //   .required(enterPriceMsg)
            //   .test(
            //     'greater-than-or-equal-to-zero',
            //     'value must be greater than or equal to 0',
            //     function (value?: number | null) {
            //       return value !== null && isNonEmptyNumber(value)
            //         ? !(value! < 0)
            //         : true
            //     }
            //   )
            //   .checkThreeDecimalpointValidation({
            //     message: threeDecimalMessage,
            //   }),
            price: Yup.number().when('type', {
              is: (type: string) => type !== 'additional_usage',
              then: Yup.number()
                .typeError(enterPriceMsg)
                .required(enterPriceMsg)

                .checkThreeDecimalpointValidation({
                  message: threeDecimalMessage,
                }),
              otherwise: Yup.number().nullable().notRequired(),
            }),
            prices: Yup.object().when('isAdditionalUsageAllowed', {
              is: (val: any) => val === true,
              then: Yup.object().shape({
                additionalUsagePrice: Yup.number()
                  .typeError('Please enter a price')
                  .required('Please enter a price')
                  .min(0, 'Price cannot be negative')
                  .checkThreeDecimalpointValidation({
                    message: threeDecimalMessage,
                  }),
              }),
              otherwise: Yup.object().nullable(),
            }),
          })
        )
      }
      // 🔹 USAGE → NO UNIQUE
      if (discountType === DiscountType.USAGE_PRICING) {
        return schema.of(
          Yup.object().shape({
            type: Yup.string(),
            prices: Yup.object().shape({
              subscriptionPrice: Yup.number()
                .typeError('Please enter a price')
                .required('Please enter a price')
                .min(0, 'Price cannot be negative')
                .checkThreeDecimalpointValidation({
                  message: threeDecimalMessage,
                }),
            }),
          })
        )
      }

      return withUnique
    }
  )

  const resaleAuthFormikValidationSchema = Yup.object().shape({
    //add validation here
    companyName: Yup.string()
      // .required('Company name cannot be empty.')
      .notRequired()
      .nullable()
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
      .max(100, 'Company name cannot exceed 100 characters.'),
    dimensions: dimensionsSaasValidation,
    agreementName: Yup.string()
      .required('Authorization name cannot be empty.')
      .nullable()
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
    emailCustomMessage: Yup.string()
      .notRequired()
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
    agreementDescription: Yup.string()
      .notRequired()
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
    agreementRecipients: buildOfferRecipientsValidation(offerRecipientMessages),
    customOfferStakeholders: Yup.array().test(
      'Enter-valid-emails',
      'Enter valid emails',
      function (value?: CustomStakeholder[]) {
        if (!value) return true
        return value?.every(v =>
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            v.email
          )
        )
      }
    ),

    notes: Yup.string(),

    autoRenew: Yup.boolean(),
    metaData: Yup.array().of(metaDataItemValidationSchema),
    resellersInfo: Yup.array()
      .of(
        Yup.object().shape({
          resellerAwsAccountId: Yup.string()
            .required(mandatoryFieldMessage)
            .matches(
              emptySpaceCharsAtStartAndEndRegex,
              `${mandatoryFieldMessage}.`
            )
            .min(12, awsAccountIdFieldValidationMessage)
            .max(12, awsAccountIdFieldValidationMessage)
            .matches(awsAccountIdRegex, awsRegexValidationMessage),
        })
      )
      .min(1, 'Select a reseller name'),
    renewal: Yup.mixed().test(
      'is-renewal-required',
      'Please select an option.',
      function (value: string | boolean) {
        if (value === undefined) {
          return false
        }
        return true
      }
    ),
    buyerAwsAccountId: Yup.string()
      .trim()
      .when('renewal', {
        is: true, // <-- when renewal is true
        then: schema =>
          schema
            .required('AWS Account ID is required for renewal.') // <-- required
            .matches(
              emptySpaceCharsAtStartAndEndRegex,
              `${mandatoryFieldMessage}.`
            )
            .length(12, awsAccountIdFieldValidationMessage)
            .matches(awsAccountIdRegex, awsRegexValidationMessage),
        otherwise: schema =>
          schema
            .nullable()
            .matches(
              emptySpaceCharsAtStartAndEndRegex,
              `${mandatoryFieldMessage}.`
            )
            .length(12, awsAccountIdFieldValidationMessage)
            .matches(awsAccountIdRegex, awsRegexValidationMessage), // <-- not required otherwise
      }),

    renewalChannel: Yup.string()
      .nullable()
      .when('renewal', {
        is: true,
        then: Yup.string().required('A renewal type is required.'),
        otherwise: Yup.string().nullable(),
      }),
    renewalPricingModel: Yup.string()
      .nullable()
      .when('renewal', {
        is: true,
        then: Yup.string()
          .nullable()
          .required('Select a previous agreement pricing model type.'),
        otherwise: Yup.string().nullable(),
      }),

    contractDurationInMonthsForEndCustomer:
      productType !== 'SAAS_SUBSCRIPTION'
        ? Yup.number()
            .typeError('Please enter a valid number')
            .required('Please enter a number')
            .positive('Duration cannot be negative')
            .integer('Duration must be a whole number')
            .min(1, 'Duration must be greater than 0')
            .test('service-length-with-payment-type', function (value) {
              if (isNil(value)) {
                return true
              }
              const contractDuration = this.parent.contractDurationTimeUnit
              if (contractDuration === 'MONTHS') {
                if (value > 144) {
                  return this.createError({
                    path: this.path,
                    message:
                      'Duration must be less than or equal to 12 years (144 months)',
                  })
                }
              }
              if (contractDuration === 'YEARS') {
                if (value > 12) {
                  return this.createError({
                    path: this.path,
                    message:
                      'Duration must be less than or equal to 12 years (144 months)',
                  })
                }
              }

              return true
            })
        : Yup.number().nullable().notRequired(),
    firstInstallmentDate: Yup.string().test(
      'date-not-greater-than-max-first-date',
      function (value) {
        if (!value) return true

        const formValues = this.options.context
        const updatedExpirationDate = formValues?.agreementExpirationDate
        const expirationDate = DateTime.fromISO(updatedExpirationDate)
        if (!expirationDate) return true

        const selected = DateTime.fromISO(value)

        if (selected < expirationDate) {
          return this.createError({
            message: `Date cannot be smaller than ${expirationDate.toFormat(
              'dd-MM-yyyy'
            )}.`,
          })
        }

        return true
      }
    ),
    installmentInfo: Yup.array().when('discountType', {
      is: (discountType: string) => {
        return discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS
      },
      then: Yup.array()
        .required('Installment info is required.')
        .of(
          Yup.object().shape({
            amount: Yup.number()
              .typeError('Please enter a valid installment amount.')
              .required('Installment amount is required.')
              .positive('Installment amount must be greater than zero.')
              .checkThreeDecimalpointValidation({
                message: threeDecimalMessage,
              }),

            paymentDate: Yup.string()
              .required('Date is required.')
              .test(
                'unique-payment-date',
                'Duplicate date is not allowed.',
                function (value) {
                  if (!value) return true

                  const formValues = this.options.context
                  const installments = formValues?.installmentInfo || []

                  const matchingDates = installments
                    .map((item: any) => item.paymentDate)
                    .filter((date: string) => date === value)

                  return matchingDates.length <= 1
                }
              )
              .test('date-not-greater-than-max', function (value) {
                if (!value) return true

                const formValues = this.options.context
                const duration =
                  formValues?.contractDurationInMonthsForEndCustomer
                const unitTime = formValues?.contractDurationTimeUnit

                if (!duration) return true

                const updatedDuration =
                  unitTime === 'YEARS' ? duration * 12 : duration

                const maxDate = DateTime.now()
                  .plus({ months: updatedDuration })
                  .minus({ days: 1 })

                const selected = DateTime.fromISO(value)

                if (selected > maxDate) {
                  return this.createError({
                    message: `Date cannot be greater than ${maxDate.toFormat(
                      'dd-MM-yyyy'
                    )}.`,
                  })
                }

                return true
              })
              .test(
                'date-not-greater-than-max-first-date-test',
                function (value) {
                  if (!value) return true

                  const formValues = this.options.context
                  const updatedExpirationDate =
                    formValues?.agreementExpirationDate
                  const expirationDate = DateTime.fromISO(updatedExpirationDate)
                  if (!expirationDate) return true

                  const selected = DateTime.fromISO(value)

                  if (selected < expirationDate) {
                    return this.createError({
                      message: `Date cannot be smaller than ${expirationDate.toFormat(
                        'dd-MM-yyyy'
                      )}.`,
                    })
                  }

                  return true
                }
              ),
          })
        ),
      otherwise: Yup.array().nullable().notRequired(),
    }),
    flexiblePaymentType: Yup.string(),
    tcv: Yup.number().when(['discountType', 'flexiblePaymentType'], {
      is: (discountType: string, flexiblePaymentType: string) => {
        return (
          discountType === DiscountType.FLEXIBLE_PAYMENT_TERMS &&
          flexiblePaymentType !== ScheduleType.CUSTOM
        )
      },
      then: Yup.number()
        .typeError('Please enter a valid total contract value (TCV).')
        .required('Total contract value (TCV) is required.')
        .positive('Total contract value must be greater than zero.'),
      otherwise: Yup.number().nullable().notRequired(),
    }),
    agreementExpirationDate: Yup.string().when('agreementDurationType', {
      is: (agreementDurationType: string) =>
        agreementDurationType !== 'NO_SET_TIME',
      then: Yup.string().required('Agreement end date is required.').nullable(),
      otherwise: Yup.string().nullable().notRequired(),
    }),
    endCustomerEulaFiles: Yup.array().when('endCustomerEulaType', {
      is: (endCustomerEulaType: string) =>
        endCustomerEulaType === EulaType.CUSTOM,
      then: Yup.array()
        .required('EULA file is required when EULA type is custom.')
        .min(1, 'At least one EULA file is required.'),
      otherwise: Yup.array().optional().notRequired(),
    }),
    resellerLicenseAgreementFiles: Yup.array().when(
      'resellerLicenseAgreementType',
      {
        is: (resellerLicenseAgreementType: string) =>
          resellerLicenseAgreementType === EulaType.CUSTOM,
        then: Yup.array()
          .required('EULA file is required when EULA type is custom.')
          .min(1, 'At least one EULA file is required.'),
        otherwise: Yup.array().optional().notRequired(),
      }
    ),
  })
  return resaleAuthFormikValidationSchema
}

export default getResaleAuthFormikValidationSchema
